import type { CrudArgs } from "./composables/ContextVoter";
import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

const readAttributes = [
  PermissionAttributesEnum.ContextOperationTranslationcontextRead,
  PermissionAttributesEnum.ContextOperationTranslationcontextReadcollectionviaclient,
  PermissionAttributesEnum.ContextOperationTranslationcontextReadcollectionvialocation,
];

export class TranslationContextVoter extends ContextVoter implements ContextVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTranslationcontextCreateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationTranslationcontextCreate,
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTranslationcontextReadany,
      contextAttribute: readAttributes,
    });
  };

  public canUpdate: ContextVoterInterface["canUpdate"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTranslationcontextUpdateany,
      contextAttribute: PermissionAttributesEnum.ContextOperationTranslationcontextUpdate,
    });
  };

  public canDelete: ContextVoterInterface["canDelete"] = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationTranslationcontextDeleteany,
      contextAttribute: PermissionAttributesEnum.ContextOperationTranslationcontextDelete,
    });
  };

  public canRetranslate(item: CrudArgs, via: "client" | "location"): boolean {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationTranslationcontextUpdateany,
    })) {
      return true;
    }

    // API grants retranslate with translation_context.update on the owner; underscore
    // retranslate_via_* attributes are never assignable on roles.
    return this.hasAccess({
      client: item.client,
      location: via === "location" ? item.location : null,
      attribute: PermissionAttributesEnum.ContextOperationTranslationcontextUpdate,
    });
  }

  public canManage: AggregateVoterCheck = (item = {
    client: this.permissionService.defaultClient,
    location: this.permissionService.defaultLocation,
  }) => {
    return this._canCrud({
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationTranslationcontextCreateany,
        PermissionAttributesEnum.GlobalOperationTranslationcontextUpdateany,
        PermissionAttributesEnum.GlobalOperationTranslationcontextDeleteany,
      ],
      contextAttribute: [
        PermissionAttributesEnum.ContextOperationTranslationcontextCreate,
        PermissionAttributesEnum.ContextOperationTranslationcontextUpdate,
        PermissionAttributesEnum.ContextOperationTranslationcontextDelete,
      ],
    });
  };

  public canAccess(item?: CrudArgs): boolean {
    return this._canCrud({
      item: item ?? {
        client: this.permissionService.defaultClient,
        location: this.permissionService.defaultLocation,
      },
      globalAttribute: PermissionAttributesEnum.GlobalOperationTranslationcontextReadany,
      contextAttribute: readAttributes,
    });
  }
}
